/**
 * Hook para gestión de delegaciones físicas
 * @module hooks/useDelegations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { delegationService } from '@/services/delegationService';
import type {
  DelegationCreate,
  DelegationUpdate,
} from '@/types/delegation.types';

export const useDelegations = () => {
  const queryClient = useQueryClient();

  // Query: Listar delegaciones
  const {
    data: delegations,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['delegations'],
    queryFn: () => delegationService.list(),
    staleTime: 300000, // 5 minutos
  });

  // Query: Delegaciones activas
  const {
    data: activeDelegations,
    isLoading: isLoadingActive,
  } = useQuery({
    queryKey: ['delegations', 'active'],
    queryFn: () => delegationService.listActive(),
    staleTime: 300000,
  });

  // Mutation: Crear delegación
  const createMutation = useMutation({
    mutationFn: (data: DelegationCreate) => delegationService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['delegations'] });
    },
  });

  // Mutation: Actualizar delegación
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: DelegationUpdate }) =>
      delegationService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['delegations'] });
    },
  });

  // Mutation: Eliminar delegación
  const deleteMutation = useMutation({
    mutationFn: (id: string) => delegationService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['delegations'] });
    },
  });

  return {
    delegations,
    activeDelegations,
    isLoading: isLoading || isLoadingActive,
    isLoadingActive,
    error,
    refetch,
    create: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
    update: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
    delete: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,
  };
};

/**
 * Hook para obtener una delegación por ID
 */
export const useDelegation = (id: string | undefined) => {
  return useQuery({
    queryKey: ['delegation', id],
    queryFn: () => {
      if (!id) throw new Error('ID requerido');
      return delegationService.getById(id);
    },
    enabled: !!id,
    staleTime: 300000,
  });
};
